import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import Banco from "../bank";
import { appStatus } from "../../store/modules/app_status/actions";
import {
  MainContainer,
  ContentWrapper,
  Row,
  Label,
  ShowInput,
  FormContainer,
  LeftColumn,
  RightColumn,
  Divider,
  Subtitle,
} from "./styles";
import { motion } from "framer-motion";
import { useHookFormMask } from "use-mask-input";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { registerUserSchema } from "../inter_register_user/registerUserValidation";
import { useNavigate } from "react-router-dom";
import PageHeader from "../page_header";
import { userService } from "../../services/userService";

/** Aceita currentUser plano (login/getMe) ou aninhado `{ user: {...} }`. */
function resolveProfileUser(currentUser, users) {
  if (!currentUser) return null;

  const nested = currentUser.user && typeof currentUser.user === "object"
    ? currentUser.user
    : null;
  const flat = nested ? null : currentUser;
  const source = nested || flat;
  if (!source) return null;

  const id = source.id ?? currentUser.id ?? nested?.id;
  if (id == null) return null;

  const fromList = users?.find((u) => String(u.id) === String(id));
  return fromList || { ...source, id };
}

function mapUserToForm(userData) {
  return {
    full_name: userData.full_name ?? "",
    rg: userData.rg ?? "",
    cpf: userData.cpf ?? "",
    cnpj: userData.cnpj ?? "",
    phone: userData.phone ?? "",
    complement: userData.complement ?? "",
    number: userData.number ?? "",
    cep: userData.cep ?? "",
    district: userData.district ?? "",
    street: userData.street ?? "",
    city: userData.city ?? "",
    state: userData.state ?? "",
    email: userData.email ?? "",
    carteira_meta_mask: userData.carteira_meta_mask ?? "",
    user_type: userData.user_type ?? "",
    accept_terms_of_use: userData.accept_terms_of_use,
    accept_privacy_politics: userData.accept_privacy_politics,
    created_at: userData.created_at,
    updated_at: userData.updated_at,
    is_active: userData.is_active,
    is_staff: userData.is_staff,
    is_superuser: userData.is_superuser,
    date_joined: userData.date_joined,
  };
}

const Profile = () => {
  const [showModalBanco, setShowModalBanco] = useState(false);
  const [profileUser, setProfileUser] = useState(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.user.currentUser);
  const users = useSelector((state) => state.app_data.users);

  const { register, reset } = useForm({
    resolver: zodResolver(registerUserSchema),
  });

  const registerWithMask = useHookFormMask(register);

  const sessionUser = resolveProfileUser(currentUser, users);
  const userId = sessionUser?.id;

  useEffect(() => {
    if (!userId) return;

    let cancelled = false;

    const applyUser = (userData) => {
      setProfileUser(userData);
      reset(mapUserToForm(userData));
    };

    const fetchUser = async () => {
      try {
        const userData = await userService.getUserById(userId);
        if (cancelled) return;
        applyUser(userData);
      } catch (error) {
        console.error("Erro ao buscar perfil:", error);
        if (cancelled) return;

        // Fallback: dados da sessão / lista local
        if (sessionUser?.id) {
          applyUser(sessionUser);
        }
      }
    };

    fetchUser();
    return () => {
      cancelled = true;
    };
    // Só recarrega quando muda o id
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, reset]);

  const handleGoToEditUser = () => {
    const userForEdit = profileUser || sessionUser;
    if (!userForEdit?.id) {
      return;
    }

    // Define o subfluxo ANTES de navegar; o shell não pode sobrescrever edit_user
    dispatch(appStatus("edit_user"));
    navigate("/welcome", {
      state: { user: userForEdit, from: "profile" },
      replace: true,
    });
  };

  const handleModalBanco = () => {
    setShowModalBanco(!showModalBanco);
  };

  return (
    <MainContainer>
      <ContentWrapper>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.8 }}
        />

        <PageHeader
          title="Meu perfil"
          actionLabel="Editar perfil"
          onAction={handleGoToEditUser}
          actionDisabled={!(profileUser?.id || sessionUser?.id)}
        />

        <FormContainer>
          <Subtitle>📌 Dados Pessoais</Subtitle>

          <LeftColumn>
            <Row>
              <Label>Nome completo:</Label>
              <ShowInput {...register("full_name")} readOnly />
            </Row>
            <Row>
              <Label>Email:</Label>
              <ShowInput {...register("email")} readOnly />
            </Row>
            <Row>
              <Label>Whatsapp:</Label>
              <ShowInput
                {...registerWithMask("phone", "(99) 99999-9999")}
                readOnly
              />
            </Row>
            <Row>
              <Label>Tipo de acesso:</Label>
              <ShowInput {...register("user_type")} readOnly />
            </Row>
          </LeftColumn>
          <RightColumn>
            <Row>
              <Label>RG:</Label>
              <ShowInput
                {...registerWithMask("rg", "99.999.999-9")}
                readOnly
              />
            </Row>
            <Row>
              <Label>CPF:</Label>
              <ShowInput
                {...registerWithMask("cpf", "999.999.999-99")}
                readOnly
              />
            </Row>
            <Row>
              <Label>CNPJ:</Label>
              <ShowInput
                {...registerWithMask("cnpj", "99.999.999/9999-99")}
                readOnly
              />
            </Row>
            <Row>
              <Label>Carteira MetaMask:</Label>
              <ShowInput {...register("carteira_meta_mask")} readOnly />
            </Row>
          </RightColumn>

          <Divider />
          <Subtitle>🏠 Endereço</Subtitle>

          <LeftColumn>
            <Row>
              <Label>CEP:</Label>
              <ShowInput {...registerWithMask("cep", "99999-999")} readOnly />
            </Row>
            <Row>
              <Label>Rua:</Label>
              <ShowInput {...register("street")} readOnly />
            </Row>
            <Row>
              <Label>Bairro:</Label>
              <ShowInput {...register("district")} readOnly />
            </Row>
            <Row>
              <Label>Cidade:</Label>
              <ShowInput {...register("city")} readOnly />
            </Row>
          </LeftColumn>
          <RightColumn>
            <Row>
              <Label>UF:</Label>
              <ShowInput {...register("state")} readOnly />
            </Row>
            <Row>
              <Label>Número:</Label>
              <ShowInput {...register("number")} readOnly />
            </Row>
            <Row>
              <Label>Complemento:</Label>
              <ShowInput {...register("complement")} readOnly />
            </Row>
          </RightColumn>
        </FormContainer>

        {showModalBanco && (
          <Banco isOpen={showModalBanco} onClose={handleModalBanco} />
        )}
      </ContentWrapper>
    </MainContainer>
  );
};

export default Profile;
